import { CustomFormatType, DimensionType, } from '../types/field';
import { FilterOperator } from '../types/filter';
import { ComparisonDiffTypes, ComparisonFormatTypes, } from '../types/savedCharts';
import assertUnreachable from '../utils/assertUnreachable';
import { applyCustomFormat, getCustomFormatFromLegacy, } from '../utils/formatting';
const NOT_APPLICABLE = 'n/a';
/** Nothing selected yet: no value means no query and an empty spec. */
const defaultFieldConfig = {
    x: undefined,
    y: [],
    groupBy: [],
};
export const calculateComparisonValue = (value, comparisonValue, format) => {
    const rawValue = value - comparisonValue;
    return format === ComparisonFormatTypes.PERCENTAGE
        ? rawValue / Math.abs(comparisonValue)
        : rawValue;
};
const isNumeric = (value) => value !== null &&
    value !== undefined &&
    value !== '' &&
    !Number.isNaN(Number(value));
const formatValue = (value, style) => {
    if (value === null || value === undefined) {
        return NOT_APPLICABLE;
    }
    if (!isNumeric(value)) {
        return String(value);
    }
    return applyCustomFormat(Number(value), getCustomFormatFromLegacy({ compact: style }));
};
const matchesRule = (value, rule) => {
    switch (rule.operator) {
        case FilterOperator.EQUALS:
            return value === rule.value;
        case FilterOperator.NOT_EQUALS:
            return value !== rule.value;
        case FilterOperator.LESS_THAN:
            return value < rule.value;
        case FilterOperator.LESS_THAN_OR_EQUAL:
            return value <= rule.value;
        case FilterOperator.GREATER_THAN:
            return value > rule.value;
        case FilterOperator.GREATER_THAN_OR_EQUAL:
            return value >= rule.value;
        default:
            return assertUnreachable(rule.operator, `Unsupported big number conditional operator`);
    }
};
export const getBigNumberValueColor = (value, rules) => {
    if (!rules?.length || !isNumeric(value)) {
        return undefined;
    }
    const match = rules.find((rule) => matchesRule(Number(value), rule));
    if (!match) {
        return undefined;
    }
    return `light-dark(${match.color}, ${match.darkColor ?? match.color})`;
};
const getComparisonDirection = (delta) => {
    if (delta > 0)
        return ComparisonDiffTypes.POSITIVE;
    if (delta < 0)
        return ComparisonDiffTypes.NEGATIVE;
    return ComparisonDiffTypes.NONE;
};
/**
 * Renders a single aggregated value from a SQL query. Unlike the other SQL
 * chart models there is no index — every row collapses into one aggregate per
 * selected field — so `fieldConfig.y[0]` is the value and `fieldConfig.y[1]`
 * is the optional comparison field.
 */
export class BigNumberDataModel {
    constructor(args) {
        this.resultsRunner = args.resultsRunner;
        this.fieldConfig = args.fieldConfig ?? defaultFieldConfig;
    }
    getResultOptions() {
        return {
            metricFieldOptions: this.resultsRunner.getPivotQueryMetrics(),
            customMetricFieldOptions: this.resultsRunner.getPivotQueryCustomMetrics(),
        };
    }
    getDefaultLayout() {
        const { metricFieldOptions, customMetricFieldOptions } = this.getResultOptions();
        const numericCustomMetrics = customMetricFieldOptions.filter((field) => field.dimensionType === DimensionType.NUMBER);
        const valueField = metricFieldOptions[0] ??
            numericCustomMetrics[0] ??
            customMetricFieldOptions[0];
        if (valueField === undefined) {
            return undefined;
        }
        return {
            x: undefined,
            y: [
                {
                    reference: valueField.reference,
                    aggregation: valueField.aggregation,
                },
            ],
            groupBy: [],
        };
    }
    mergeConfig(chartKind, currentVizConfig) {
        const newDefaultLayout = this.getDefaultLayout();
        const currentFields = currentVizConfig?.fieldConfig?.y ?? [];
        const currentLayoutIsStillValid = currentFields.length > 0 &&
            this.getUnknownReferences(currentFields).length === 0;
        return {
            metadata: { version: 1 },
            type: chartKind,
            fieldConfig: currentLayoutIsStillValid
                ? currentVizConfig?.fieldConfig
                : (newDefaultLayout ?? currentVizConfig?.fieldConfig),
            display: currentVizConfig?.display ?? {},
        };
    }
    /**
     * References in `y` that the current results can't provide, either as a
     * pre-aggregated metric or as a custom metric over a dimension.
     */
    getUnknownReferences(fields) {
        const { metricFieldOptions, customMetricFieldOptions } = this.getResultOptions();
        const knownReferences = new Set([...metricFieldOptions, ...customMetricFieldOptions].map((option) => option.reference));
        return fields
            .map((field) => field.reference)
            .filter((reference) => !knownReferences.has(reference));
    }
    getConfigErrors(config) {
        if (!config) {
            return undefined;
        }
        const unknownReferences = this.getUnknownReferences(config.y ?? []);
        return unknownReferences.length > 0
            ? { metricFieldError: { references: unknownReferences } }
            : undefined;
    }
    async getPivotedChartData({ sortBy, filters, limit, sql, }) {
        const allDimensionNames = new Set(this.resultsRunner
            .getPivotQueryDimensions()
            .map((d) => d.reference));
        const { customMetrics, metrics } = (this.fieldConfig.y ?? []).reduce((acc, field) => {
            if (allDimensionNames.has(field.reference)) {
                const name = `${field.reference}_${field.aggregation}`;
                acc.customMetrics.push({
                    name,
                    baseDimension: field.reference,
                    aggType: field.aggregation,
                });
                acc.metrics.push({ name });
            }
            else {
                acc.metrics.push({ name: field.reference });
            }
            return acc;
        }, { customMetrics: [], metrics: [] });
        const query = {
            sql,
            limit,
            filters,
            sortBy,
            metrics,
            dimensions: [],
            timeDimensions: [],
            pivot: {
                index: [],
                on: [],
                values: metrics.map((metric) => metric.name),
            },
            customMetrics,
        };
        this.pivotedChartData =
            await this.resultsRunner.getPivotedVisualizationData(query);
        return this.pivotedChartData;
    }
    getPivotedTableData() {
        const transformedData = this.pivotedChartData;
        if (!transformedData) {
            return undefined;
        }
        return {
            columns: Object.keys(transformedData.results[0] ?? {}),
            rows: transformedData.results,
        };
    }
    getDataDownloadUrl() {
        return this.pivotedChartData?.fileUrl;
    }
    /**
     * Compares the value against the second selected field. Returns undefined
     * when there is nothing to compare against.
     */
    static buildComparison(value, comparisonValue, comparisonField, display) {
        if (!isNumeric(value) || !isNumeric(comparisonValue)) {
            return {
                value: undefined,
                formattedValue: NOT_APPLICABLE,
                direction: comparisonValue === undefined || comparisonValue === null
                    ? ComparisonDiffTypes.UNDEFINED
                    : ComparisonDiffTypes.NAN,
                label: display.comparisonLabel,
                tooltip: 'Comparison field has no comparable value',
            };
        }
        const format = display.comparisonFormat ?? ComparisonFormatTypes.RAW;
        const delta = calculateComparisonValue(Number(value), Number(comparisonValue), format);
        const direction = getComparisonDirection(delta);
        // Only a decrease carries its own sign.
        const prefix = direction === ComparisonDiffTypes.NEGATIVE ? '' : '+';
        const formattedValue = format === ComparisonFormatTypes.PERCENTAGE
            ? `${prefix}${applyCustomFormat(delta, {
                round: 0,
                type: CustomFormatType.PERCENT,
            })}`
            : `${prefix}${formatValue(delta, display.style)}`;
        return {
            value: delta,
            formattedValue,
            direction,
            label: display.comparisonLabel,
            tooltip: direction === ComparisonDiffTypes.NONE
                ? `No change compared to ${comparisonField}`
                : `${formattedValue} compared to ${comparisonField}`,
        };
    }
    getSpec(display) {
        const transformedData = this.pivotedChartData;
        if (!transformedData) {
            return undefined;
        }
        const [valueColumn, comparisonColumn] = transformedData.valuesColumns;
        const row = transformedData.results[0];
        const value = valueColumn && row ? row[valueColumn.pivotColumnName] : undefined;
        const showComparison = !!display?.showComparison && !!comparisonColumn && !!row;
        return {
            value,
            formattedValue: formatValue(value, display?.style),
            label: display?.label ||
                this.fieldConfig.y?.[0]?.reference ||
                valueColumn?.referenceField ||
                '',
            showLabel: display?.showLabel ?? true,
            flipColors: display?.flipColors ?? false,
            valueColor: getBigNumberValueColor(value, display?.conditionalFormatting),
            comparison: showComparison
                ? BigNumberDataModel.buildComparison(value, row[comparisonColumn.pivotColumnName], comparisonColumn.referenceField, display)
                : undefined,
        };
    }
}
//# sourceMappingURL=BigNumberDataModel.js.map